import { logger } from "@/lib/logger";
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CACHE_KEYS } from '@/lib/queryClient';

export interface SiteTierDefinition {
  id: string;
  tier_name: 'site_free' | 'site_clinical' | 'site_revenue';
  display_name: string;
  description: string | null;
  monthly_price_cents: number;
  currency: string;
  enable_investigator_dashboard: boolean;
  enable_visit_tracking: boolean;
  enable_omop_sdtm_export: boolean;
  enable_cro_integration: boolean;
  enable_billing_dashboard: boolean;
  enable_protocol_fee_schedules: boolean;
  enable_invoice_generation: boolean;
  max_patients: number | null;
  max_staff: number | null;
  max_trials: number | null;
  is_active: boolean;
  sort_order: number;
}

export interface SiteSubscription {
  id: string;
  trial_site_id: string;
  tier: 'site_free' | 'site_clinical' | 'site_revenue';
  status: 'trial' | 'active' | 'past_due' | 'canceled' | 'expired';
  trial_start_date: string | null;
  trial_end_date: string | null;
  billing_email: string | null;
  billing_cycle: 'monthly' | 'annual';
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  subscribed_at: string;
  canceled_at: string | null;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SiteUsageLimits {
  tier: string;
  max_patients: number | null;
  max_staff: number | null;
  max_trials: number | null;
  current_patients: number;
  current_staff: number;
  current_trials: number;
}

/**
 * Hook to fetch site subscription data
 * Hardened with retry logic and graceful error handling
 */
export function useSiteSubscription(siteId: string | undefined) {
  return useQuery({
    queryKey: [CACHE_KEYS.trialSites, 'subscription', siteId],
    queryFn: async () => {
      // Return null if no siteId (don't throw)
      if (!siteId) {
        logger.debug('[useSiteSubscription] No siteId provided, returning null');
        return null;
      }

      const startTime = performance.now();
      
      try {
        const { data, error } = await supabase
          .from('trial_site_subscriptions' as any)
          .select('*')
          .eq('trial_site_id', siteId)
          .in('status', ['trial', 'active'])
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        const duration = Math.round(performance.now() - startTime);

        if (error) {
          logger.error('[useSiteSubscription] Query failed:', {
            siteId,
            error: error.message,
            code: error.code,
            duration
          });
          // Return null instead of throwing to prevent crash
          return null;
        }

        const subscription = data as unknown as SiteSubscription | null;
        logger.debug('[useSiteSubscription] Success:', {
          siteId,
          tier: subscription?.tier || 'none',
          duration
        });

        return subscription;
      } catch (e) {
        const duration = Math.round(performance.now() - startTime);
        logger.error('[useSiteSubscription] Exception:', {
          siteId,
          error: e,
          duration
        });
        // Return null on exception to prevent crash
        return null;
      }
    },
    enabled: !!siteId,
    staleTime: 60 * 1000, // 1 minute
    retry: 2, // Retry twice on failure
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000), // Exponential backoff
  });
}

/**
 * Hook to fetch all available tier definitions
 */
export function useTierDefinitions() {
  return useQuery({
    queryKey: [CACHE_KEYS.trialSites, 'tier-definitions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('trial_site_tier_definitions' as any)
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) {
        logger.error('[useTierDefinitions] Query failed:', error);
        throw error;
      }
      return data as unknown as SiteTierDefinition[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes (tier definitions rarely change)
    retry: 2,
  });
}

/**
 * Hook to check if site has access to a specific feature
 * Hardened to "fail open" (allow access) on error to prevent blocking users
 */
export function useSiteFeatureAccess(siteId: string | undefined, featureName: string) {
  return useQuery({
    queryKey: [CACHE_KEYS.trialSites, 'feature-access', siteId, featureName],
    queryFn: async () => {
      // Default to allow access if no siteId
      if (!siteId) {
        logger.debug('[useSiteFeatureAccess] No siteId, defaulting to allow');
        return true;
      }

      const startTime = performance.now();

      try {
        const { data, error } = await supabase.rpc('site_has_feature_access' as any, {
          p_trial_site_id: siteId,
          p_feature_name: featureName,
        });

        const duration = Math.round(performance.now() - startTime);

        if (error) {
          logger.warn('[useSiteFeatureAccess] RPC failed, allowing access:', {
            siteId,
            featureName,
            error: error.message,
            duration
          });
          // Fail open - allow access on error to prevent blocking users
          return true;
        }

        logger.debug('[useSiteFeatureAccess] Success:', {
          siteId,
          featureName,
          hasAccess: data,
          duration
        });

        return data as boolean;
      } catch (e) {
        const duration = Math.round(performance.now() - startTime);
        logger.warn('[useSiteFeatureAccess] Exception, allowing access:', {
          siteId,
          featureName,
          error: e,
          duration
        });
        // Fail open - allow access on exception
        return true;
      }
    },
    enabled: !!siteId && !!featureName,
    staleTime: 60 * 1000, // 1 minute
    retry: 1, // Only retry once for feature checks
    retryDelay: 500,
  });
}

/**
 * Hook to fetch site usage limits and current usage
 */
export function useSiteUsageLimits(siteId: string | undefined) {
  return useQuery({
    queryKey: [CACHE_KEYS.trialSites, 'usage-limits', siteId],
    queryFn: async () => {
      if (!siteId) return null;

      try {
        const { data, error } = await supabase.rpc('get_site_usage_limits' as any, {
          p_trial_site_id: siteId,
        });

        if (error) {
          logger.error('[useSiteUsageLimits] RPC failed:', error);
          return null;
        }
        return data as SiteUsageLimits;
      } catch (e) {
        logger.error('[useSiteUsageLimits] Exception:', e);
        return null;
      }
    },
    enabled: !!siteId,
    staleTime: 30 * 1000, // 30 seconds
    retry: 1,
  });
}

/**
 * Hook to fetch subscription history
 */
export function useSiteSubscriptionHistory(siteId: string | undefined) {
  return useQuery({
    queryKey: [CACHE_KEYS.trialSites, 'subscription-history', siteId],
    queryFn: async () => {
      if (!siteId) return [];

      try {
        const { data, error } = await supabase
          .from('trial_site_subscription_history' as any)
          .select('*')
          .eq('trial_site_id', siteId)
          .order('created_at', { ascending: false })
          .limit(50);

        if (error) {
          logger.error('[useSiteSubscriptionHistory] Query failed:', error);
          return [];
        }
        return data;
      } catch (e) {
        logger.error('[useSiteSubscriptionHistory] Exception:', e);
        return [];
      }
    },
    enabled: !!siteId,
    staleTime: 60 * 1000, // 1 minute
    retry: 1,
  });
}

/**
 * Helper to get formatted price
 */
export function formatTierPrice(priceInCents: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(priceInCents / 100);
}

/**
 * Helper to check if usage is within limits
 */
export function isWithinUsageLimits(limits: SiteUsageLimits): {
  patients: boolean;
  staff: boolean;
  trials: boolean;
  overall: boolean;
} {
  return {
    patients: limits.max_patients === null || limits.current_patients <= limits.max_patients,
    staff: limits.max_staff === null || limits.current_staff <= limits.max_staff,
    trials: limits.max_trials === null || limits.current_trials <= limits.max_trials,
    overall:
      (limits.max_patients === null || limits.current_patients <= limits.max_patients) &&
      (limits.max_staff === null || limits.current_staff <= limits.max_staff) &&
      (limits.max_trials === null || limits.current_trials <= limits.max_trials),
  };
}
